const NODE_GROUPING_PREFIX = 'node_group';
export const NODE_GROUPING_EVENT_NAMES = {
  CREATED: `${NODE_GROUPING_PREFIX}_created`,
  DELETED: `${NODE_GROUPING_PREFIX}_deleted`,
  UNGROUPED: `${NODE_GROUPING_PREFIX}_ungrouped`,
  ATTACHED: `${NODE_GROUPING_PREFIX}_attached`,
  DETACHED: `${NODE_GROUPING_PREFIX}_detached`,
  COLOR_CHANGED: `${NODE_GROUPING_PREFIX}_color_changed`,
  RENAMED: `${NODE_GROUPING_PREFIX}_renamed`,
  LABEL_SIZE_CHANGED: `${NODE_GROUPING_PREFIX}_label_size_changed`,
};

const PROMPT_VARIABLE_PREFIX = 'prompt_variables';
export const PROMPT_VARIABLE_EVENT_NAMES = {
  ADD_VARIABLE: `${PROMPT_VARIABLE_PREFIX}_add_variable`,
  CREATE_VARIABLE: `${PROMPT_VARIABLE_PREFIX}_create_variable`,
};

const VOICE_DICTATION_PREFIX = 'voice_dictation';
/** All but `SETTING_CHANGED` carry `surface`, the text field dictated into: `prompt_node`, `text_node` or `agent_chat`. */
export const VOICE_DICTATION_EVENT_NAMES = {
  STARTED: `${VOICE_DICTATION_PREFIX}_started`,
  /** Carries the recording length and transcript length, never the transcript. */
  INSERTED: `${VOICE_DICTATION_PREFIX}_inserted`,
  /** Carries the stage the user cancelled in: recording or transcribing. */
  CANCELLED: `${VOICE_DICTATION_PREFIX}_cancelled`,
  /** Carries why: mic blocked or unavailable, too short, no speech, rate limited, or transcription failed. */
  FAILED: `${VOICE_DICTATION_PREFIX}_failed`,
  /** A workspace admin turned voice input on or off. */
  SETTING_CHANGED: `${VOICE_DICTATION_PREFIX}_setting_changed`,
};

const PROMPT_SUGGESTION_PREFIX = 'prompt_suggestion';
export const PROMPT_SUGGESTION_EVENT_NAMES = {
  /**
   * The user pressed Tab to accept the prompt suggested behind an empty prompt node. Carries the
   * modality that produced the copy and what the prompt feeds, so we can tell which targets people
   * accept a suggestion for.
   */
  APPLIED: `${PROMPT_SUGGESTION_PREFIX}_applied`,
};

/** Reported as the suggestion's modality when the prompt has no target to take one from. */
export const PROMPT_SUGGESTION_NO_MODALITY = 'none';

export const PACK_UNPACK_EVENT_NAMES = {
  UNPACK_NODE: `Click unpack node`,
  CREATE_ITERATOR_FROM_NODE: `Click create iterator from node`,
  CREATE_ITERATOR_FROM_GROUP: `Click create iterator from group`,
};

/** Drag media from a node grid onto the workflow canvas (new import node). Split by Alt/Option = duplicate vs default = move. */
export const MEDIA_TO_CANVAS_DRAG_EVENT_NAMES = {
  /** Alt/Option at drag start: new import node; media remains on the source node. */
  DUPLICATE: `Duplicate media from node to canvas`,
  /** Default drag: new import node; remove from the source node. */
  MOVE: `Move media from node to canvas`,
};

const CANCEL_PLAN_PREFIX = 'cancel_plan';
export const CANCEL_PLAN_EVENT_NAMES = {
  DIALOG_OPENED: `${CANCEL_PLAN_PREFIX}_dialog_opened`,
  CONFIRMED: `${CANCEL_PLAN_PREFIX}_confirmed`,
  KEPT_PLAN: `${CANCEL_PLAN_PREFIX}_kept_plan`,
};

export const UNIVERSAL_UPGRADE_BADGE_EVENT_NAMES = {
  ENTRYPOINT_CLICKED: 'universal_upgrade_entrypoint_clicked',
  DISMISSED: 'universal_upgrade_badge_dismissed',
};

export const PRICING_TABLE_EVENT_NAMES = {
  CONTACT_SALES_CLICKED: 'pricing_table_contact_sales_clicked',
};

export const MODEL_SETTING_CHANGED_EVENT = 'model_setting_changed';

export const CUSTOM_MODEL_APPROVAL_EVENT_NAMES = {
  ADDED_BY_USER: 'pending_custom_model_added_by_user',
  ADDED_BY_ADMIN: 'custom_model_added_by_admin',
  REQUESTED_APPROVAL: 'pending_custom_model_requested_approval',
  REQUEST_APPROVED: 'custom_model_request_approved',
  REQUEST_DECLINED: 'custom_model_request_declined',
  SETTING_TOGGLED: 'custom_model_approval_setting_toggled',
};

export const CUSTOM_MODEL_APPROVAL_SOURCES = {
  PENDING_REQUESTS_CAROUSEL: 'pending_requests_carousel',
  WORKSPACE_MODELS_TABLE: 'workspace_models_table',
};

const WORKFLOW_COMMUNITY_PUBLISH_PREFIX = 'workflow_community_publish';
export const WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES = {
  ENTRYPOINT_VIEWED: `${WORKFLOW_COMMUNITY_PUBLISH_PREFIX}_entrypoint_viewed`,
  ENTRYPOINT_CLICKED: `${WORKFLOW_COMMUNITY_PUBLISH_PREFIX}_entrypoint_clicked`,
  MODAL_OPENED: `${WORKFLOW_COMMUNITY_PUBLISH_PREFIX}_modal_opened`,
  STEP_COMPLETED: `${WORKFLOW_COMMUNITY_PUBLISH_PREFIX}_step_completed`,
  VALIDATION_FAILED: `${WORKFLOW_COMMUNITY_PUBLISH_PREFIX}_validation_failed`,
  SUBMITTED: `${WORKFLOW_COMMUNITY_PUBLISH_PREFIX}_submitted`,
  RESULT: `${WORKFLOW_COMMUNITY_PUBLISH_PREFIX}_result`,
  ACCESS_UPDATE_RESULT: `${WORKFLOW_COMMUNITY_PUBLISH_PREFIX}_access_update_result`,
  FIGMA_LINK_RESULT: `${WORKFLOW_COMMUNITY_PUBLISH_PREFIX}_figma_link_result`,
  LINK_ACTION_CLICKED: `${WORKFLOW_COMMUNITY_PUBLISH_PREFIX}_link_action_clicked`,
  UPGRADE_CLICKED: `${WORKFLOW_COMMUNITY_PUBLISH_PREFIX}_upgrade_clicked`,
  UNPUBLISH_DIALOG_OPENED: 'workflow_community_unpublish_dialog_opened',
  UNPUBLISH_RESULT: 'workflow_community_unpublish_result',
  /**
   * The async tail of a publish. `RESULT` only means Community accepted the POST — the listing is not
   * discoverable until both moderation gates open, minutes later, and the two can diverge for good.
   * These two are the only events that say which way it ended.
   */
  LIVE: `${WORKFLOW_COMMUNITY_PUBLISH_PREFIX}_live`,
  REJECTED: `${WORKFLOW_COMMUNITY_PUBLISH_PREFIX}_rejected`,
};

// New-canvas FTUE (first-time user experience) BI events: the welcome modal and the subflows gallery. The
// event name strings are fixed by the BI spec (WEA-4894) and don't share a single prefix, so they're
// spelled out verbatim. Every event also carries `workflowId` (matching the existing analytics convention,
// e.g. WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES).
export const NEW_CANVAS_FTUE_EVENT_NAMES = {
  WELCOME_GET_STARTED_CLICKED: 'welcome_message_get_started_clicked',
  // gallery became visible; the denominator for the clicked/dismissed/closed events below.
  SUBFLOWS_SHOWN: 'ftue_subflows_shown',
  SUBFLOW_CLICKED: 'ftue_subflow_clicked',
  // explicit dismiss via the gallery's X button.
  SUBFLOWS_DISMISSED: 'ftue_subflows_dismissed',
  // gallery auto-closed because the user started building on their own (node added via drag-drop or
  // context menu) instead of picking a subflow.
  SUBFLOWS_CLOSED_NODE_ADDED: 'ftue_subflows_closed_node_added',
};

/** Fired once a resize gesture on a node ends, not while the pointer is still moving. */
export const NODE_RESIZE_EVENT_NAMES = {
  RESIZED: 'node_resized',
};

export const HANDLE_COLLAPSE_EVENT_NAMES = {
  MODE_TOGGLED: 'node_handle_collapse_toggled',
  GROUP_TOGGLED: 'node_handle_group_toggled',
};

export const MOBILE_ACTIVATION_HANDOFF_EVENT_NAMES = {
  PAGE_VIEWED: 'mobile_activation_handoff_page_viewed',
  SHARE_STARTED: 'mobile_activation_handoff_share_started',
  SHARE_COMPLETED: 'mobile_activation_handoff_share_completed',
  SHARE_CANCELED: 'mobile_activation_handoff_share_canceled',
  SHARE_FAILED: 'mobile_activation_handoff_share_failed',
  LINK_COPIED: 'mobile_activation_handoff_link_copied',
  LINK_COPY_FAILED: 'mobile_activation_handoff_link_copy_failed',
};

// Custom Effect (custom_glsl) node generation lifecycle. The prefix matches the persisted node type key.
export const CUSTOM_GLSL_EVENT_NAMES = {
  GENERATION_STARTED: 'custom_glsl_generation_started',
  GENERATION_SUCCEEDED: 'custom_glsl_generation_succeeded',
  GENERATION_FAILED: 'custom_glsl_generation_failed',
};

// Free badge in weave files
export const FREE_BADGE_EVENT_NAMES = {
  ENTRYPOINT_CLICKED: 'free_badge_entrypoint_clicked',
};

/** A node landed on the canvas, however it got there. Carries `model` when the node runs one. */
export const ADDED_NEW_NODE_EVENT = 'added_new_node';
export const MARKETING_CONSENT_EVENT_NAMES = {
  ANSWERED: 'marketing_consent_answered',
};

export const MARKETING_CONSENT_SOURCE_MODAL = 'modal';

/** A Run / Re-run click in the tool viewer; `initial` separates the two. */
export const TOOL_RUN_EVENT = 'tool_run';
export const TOOL_PUBLISH_CLICKED_EVENT = 'tool_publish_clicked';
export const TOOL_PUBLISH_SUCCEEDED_EVENT = 'tool_publish_succeeded';

export const TOOL_PREVIEW_EVENT_NAMES = {
  OPENED: 'tool_preview_opened',
  BACK_TO_EDITING_CLICKED: 'tool_preview_back_to_editing_clicked',
};

/** Tool tab builder / preview BI (WEA-7464). Server owns deploy + run completion. */
export const TOOL_TAB_EVENT_NAMES = {
  TAB_OPENED: 'tool_tab_opened',
  OUTPUT_CHANGED: 'tool_output_changed',
  PREVIEW_CLICKED: 'tool_preview_clicked',
  PUBLISH_CLICKED: 'tool_publish_clicked',
  AUDIENCE_TOGGLED: 'workflow_tool_audience_toggled',
} as const;

export const AUDIT_TRAIL_EXPORT_EVENT = 'audit_trail_exported';

export type AuditTrailExportSurface = 'file_browser' | 'workflow_menu' | 'node_menu';

export type AuditTrailExportScope = 'workflow' | 'node' | 'asset';

export type AuditTrailExportActor = 'admin' | 'owner';

export const WEAVE_PAYWALL_EVENT_NAMES = {
  OPENED: 'weave_paywall_opened',
  VIEWED: 'weave_paywall_viewed',
  BILLING_CYCLE_CHANGED: 'weave_paywall_billing_cycle_changed',
  CTA_CLICKED: 'weave_paywall_cta_clicked',
  CHECKOUT_RESULT: 'weave_paywall_checkout_result',
  DISMISSED: 'weave_paywall_dismissed',
};

export const EMPTY_CANVAS_EVENT_NAMES = {
  ADD_PROMPT_NODE_CLICKED: 'empty_canvas_add_prompt_node_clicked',
  OPEN_TEMPLATE_PICKER_CLICKED: 'empty_canvas_open_template_picker_clicked',
  TEMPLATE_SELECTED: 'empty_canvas_template_selected',
  TEMPLATE_PICKER_CLOSED: 'empty_canvas_template_picker_closed',
  FIGMA_COMMUNITY_CLICKED: 'empty_canvas_figma_community_clicked',
};

// Weave API settings BI events (WEA-7366).
export const WEAVE_API_EVENT_NAMES = {
  TERMS_OPENED: 'api_dev_terms_consent_opened',
  TERMS_APPROVED: 'api_dev_terms_consent_approved',
  TERMS_DECLINED: 'api_dev_terms_consent_declined',
  TOKEN_GENERATED: 'api_token_generated',
  TOKEN_REVOKED: 'api_token_revoked',
} as const;

/** Workspace fonts were stored from Settings → Fonts. Carries how many fonts were stored and how many failed. */
export const WORKSPACE_FONT_UPLOADED_EVENT = 'workspace_font_uploaded';

/** A keyed menu action completed or failed. */
export const CONTEXT_MENU_ACTION_CLICKED_EVENT = 'context_menu_action_clicked';

export const VIDEO_CONCATENATOR_EVENT_NAMES = {
  SCALE_MODE_CHANGED: 'video_concatenator_scale_mode_changed',
  FILMSTRIP_REORDERED: 'video_concatenator_filmstrip_reordered',
};
